import {Platform} from 'react-native';
import type {LinkingOptions} from '@react-navigation/native';

import type {RootStackParamList} from '@/app/routes';

/**
 * Адреса.
 *
 * Без этого файла веб-сборка — один адрес на всё приложение: любой экран это `/`,
 * кнопка «назад» уводит из приложения вместо шага назад, F5 роняет на главную что бы
 * ни было открыто, и ничем нельзя поделиться. Для телефонного приложения, которое
 * заодно открывается в браузере, это терпимо; для того, что ставят на домашний экран
 * и используют как приложение, — нет: там «назад» это и есть кнопка браузера, а
 * «перезагрузить» — то, чем люди чинят всё.
 *
 * Пути названы по-человечески и совпадают с тем, что написано в боковом меню: `/journal`,
 * `/staff`, `/attendance`, `/devices`, `/settings`. Ярлыки манифеста
 * (web/public/manifest.webmanifest) указывают ровно сюда.
 *
 * Набор вкладок зависит от активного КОНТЕКСТА (см. `tabsFor`), поэтому путь
 * разрешается только в той персоне, у которой такая вкладка есть. Открыть `/staff`,
 * стоя в личном пространстве, не во что — и это правильно: адрес называет экран, а не
 * право на него.
 *
 * Нативу достаётся `undefined`: схемы глубоких ссылок не зарегистрировано ни в
 * AndroidManifest, ни в Info.plist.
 */
/**
 * Экспортируется, чтобы тест мог сверить её с картой родства экранов
 * ([`shared/config/screens.ts`](../shared/config/screens.ts)): адреса и «назад»
 * описывают одну и ту же иерархию, и разъехаться им нельзя.
 */
export const linkingConfig: LinkingOptions<RootStackParamList>['config'] = {
  screens: {
    Login: 'login',
    Register: 'register',
    VerifyEmail: 'register/verify',
    Forgot: 'forgot',
    Onboarding: 'onboarding',
    Main: {
      screens: {
        // ── Главная: пульт дня ─────────────────────────────────────────
        Home: {
          screens: {
            Overview: '',
          },
        },
        // ── Журнал: все проходы таблицей ───────────────────────────────
        Journal: {
          screens: {
            Journal: 'journal',
          },
        },
        // ── Личное пространство ────────────────────────────────────────
        People: {
          screens: {
            PeopleList: 'people',
            Person: 'people/:recordId',
          },
        },
        // ── Компания: сотрудники ───────────────────────────────────────
        Personnel: {
          screens: {
            PersonnelList: 'staff',
            PersonnelDetached: 'staff/detached',
            PersonnelCard: 'staff/:recordId',
            PersonnelForm: 'staff/:recordId/edit',
          },
        },
        // ── Компания: посещаемость и начисления ────────────────────────
        Reports: {
          screens: {
            ReportsOverview: 'attendance',
            DepartmentReport: 'attendance/department/:deptId',
            EmployeeReport: 'attendance/employee/:recordId',
            Payroll: 'attendance/payroll',
          },
        },
        // ── График работы: месяц календарём ────────────────────────────
        Schedule: 'schedule',
        // ── Общая для личного пространства и компании ──────────────────
        Devices: {
          screens: {
            DeviceList: 'devices',
            Device: 'devices/:deviceId',
            DeviceSettings: 'devices/:deviceId/settings',
            ScanDevice: 'devices/scan',
            ConfirmAdopt: 'devices/adopt',
          },
        },
        // ── Камеры: просмотр и настройки камер ─────────────────────────
        Cameras: {
          screens: {
            CameraWall: 'cameras',
            CameraSettings: 'cameras/:sourceId/settings',
          },
        },
        // ── Компания из одних камер: отделы своей вкладкой ─────────────
        Org: {
          screens: {
            Departments: 'departments',
            Department: 'departments/:deptId',
          },
        },
        // ── Персона «сотрудник» ────────────────────────────────────────
        MyAttendance: 'my-attendance',
        MyLogs: 'my-logs',
        // ── Есть у всех ────────────────────────────────────────────────
        Settings: {
          screens: {
            Settings: 'settings',
            Departments: 'settings/departments',
            Department: 'settings/departments/:deptId',
            Members: 'settings/members',
            Telegram: 'settings/telegram',
            NotificationSettings: 'settings/notifications',
            CompanySettings: 'settings/company',
            CalendarSettings: 'settings/calendar',
            TerminalProfiles: 'settings/terminals',
            ApiBridge: 'settings/integration',
            Account: 'settings/account',
            Security: 'settings/security',
            PushSettings: 'settings/push',
          },
        },
      },
    },
  },
};

export const linking: LinkingOptions<RootStackParamList> | undefined =
  Platform.OS === 'web'
    ? {
        // Только свой origin: браузерная сборка не принимает ссылок ниоткуда ещё.
        prefixes: [],
        config: linkingConfig,
      }
    : undefined;

/**
 * Заголовок вкладки браузера. Историю из тридцати одинаковых «SamVision» листать
 * невозможно, поэтому в заголовок идёт имя раздела, которое навигатор и так знает.
 */
export const documentTitle = {
  formatter: (options?: {title?: string}) =>
    options?.title ? `${options.title} — SamVision` : 'SamVision',
};
